import {ChangeDetectorRef, Component, EventEmitter, Input, Output} from '@angular/core';
import {DetectMethodChanges} from '../utils';
import {
    allMembersPreviewed,
    distinctUnpreviewedExpirationCount,
    loadAllGateBlocked,
    OpgTradeSuggestion
} from './opg-copilot-domain';
import {COPILOT_DISCLAIMER} from './copilot-disclaimer';
import {COPILOT_DISCLAIMER_ENABLED} from './copilot-disclaimer-visibility';
import {IOpgCopilotFacade} from './opg-copilot-facade';

// Trade stack (CR-20260710-001, sketch frame 04; renamed at FR-14): ONE side's result
// members, rendered as a STACK of ets-opg-trade-card members — each member individually
// previewable card-by-card (the card owns its Preview/Un-Preview toggle and its
// quote-driven live price; this component only BUBBLES the event unchanged) — plus a
// footer Load-All bar.
//
// Footer logic (WI-7): while any member is not yet previewed the bar reads
// "Preview All (N)" (N = total member count) and emits loadAllRequested — BUT it is DISABLED (with a reason)
// whenever the un-previewed members span more than one bound expiration, because the single-date
// grid physically cannot hold mixed dates (a mixed set is enumerated card-by-card instead, each
// preview navigating per WI-6). When the remaining set shares one date the bar enables and the
// orchestrator loads them in one move (switching the panel to that date at most once). Once ALL
// members are previewed it flips to "Un-Preview All" (never gated) and emits unPreviewAllRequested.
// The fully-previewed state is DERIVED from the members via allMembersPreviewed() (never
// stored — members carry their own previewed flags).
//
// Prohibitions honored (spec §10): NO aggregate set price anywhere ("which strike would
// I show the price for?"); no Review button; courier principle — members render verbatim in
// skill order (no cap / filter / re-rank / reshape here).
//
// DEFAULT change detection ON PURPOSE (family leaf-owns-CD rule): [members] is a stable
// reference whose member previewed-flags mutate IN PLACE (orchestrator preview/Load-All/
// host-removal flows) — under OnPush the derived footer label went stale and its click
// could execute the OPPOSITE action (a stale "Un-Preview All" re-deriving to Load-All).
// With default CD the orchestrator's decorated detectChanges() reaches this footer.
@Component({
    selector: 'ets-opg-trade-stack',
    templateUrl: './opg-trade-stack.component.html',
    styleUrls: ['./opg-trade-stack.component.scss']
})
export class OpgTradeStackComponent {

    constructor(private _changeDetector: ChangeDetectorRef) {
    }

    // M8: the shared compliance disclaimer, which REPLACED this bar's old action hint. A constant,
    // never reassigned — exposed as a field only because templates cannot import.
    readonly disclaimer = COPILOT_DISCLAIMER;

    // CR-20260723-003: global visibility switch for the disclaimer above (client-requested disable).
    // HIDDEN, not removed - flip COPILOT_DISCLAIMER_ENABLED to re-show the disclaimer at all five sites.
    readonly disclaimerEnabled = COPILOT_DISCLAIMER_ENABLED;

    // The member suggestions this stack renders (host-minted identity, §9) — one ranked
    // member list; this component owns the Load-All bar for whichever set the orchestrator
    // routes here.
    @Input() members: OpgTradeSuggestion[] = [];

    // Host facade — passed THROUGH to the member cards (quote subscriptions, symbol);
    // this component itself never calls into it.
    @Input() root: IOpgCopilotFacade = null;

    // Whether the footer Load-All bar renders at all — false under a 'pick'-model host
    // (owner ruling 2026-08-04): with one previewed hedge per date/side the Load-All face is
    // meaningless and the Un-Preview-All face is unreachable (all-previewed never holds).
    @Input() showLoadAll = true;

    // Loaded-grid-cell price lookup by suggestionId — passed THROUGH to the member cards
    // (previewed AND priced ⇒ the grid cell's price wins; the grid is the tradeable
    // reference, §9). null while the cell is unpriced (e.g. Live off).
    @Input() loadedPriceOf: (suggestionId: string) => number | null = null;

    // A member card's Preview/Un-Preview intent, bubbled UNCHANGED (the orchestrator does
    // the actual load/unload/border work).
    @Output() previewToggled = new EventEmitter<OpgTradeSuggestion>();

    // Load-All bar intents (A5): the members list to load / un-preview. The orchestrator loops
    // every not-yet-previewed member / un-previews all members; this component never touches
    // member state itself.
    @Output() loadAllRequested = new EventEmitter<OpgTradeSuggestion[]>();
    @Output() unPreviewAllRequested = new EventEmitter<OpgTradeSuggestion[]>();

    get hasMembers(): boolean {
        return this.members.length > 0;
    }

    // DERIVED set-level state: true only when EVERY member is previewed.
    get isFullyPreviewed(): boolean {
        return allMembersPreviewed(this.members);
    }

    get loadAllLabel(): string {
        return this.isFullyPreviewed
            ? 'Un-Preview All'
            : 'Preview All (' + this.members.length + ')';
    }

    // M8: loadAllHint removed. Its two strings ("loads every not-yet-previewed trade at once" /
    // "un-previews every loaded trade at once") were narration of the button's own label, and the
    // owner replaced that slot with the compliance disclaimer — interpolated from the shared
    // COPILOT_DISCLAIMER constant since R8, not a literal in the template.
    // The getter had exactly one consumer — that template line — so it is deleted, not orphaned.

    // WI-7 gate: distinct bound-expirations still to load — drives the disabled reason text.
    get unpreviewedExpirationCount(): number {
        return distinctUnpreviewedExpirationCount(this.members);
    }

    // WI-7: the Load-All button is DISABLED whenever the remaining (un-previewed) members span more
    // than one expiration — the single-date grid cannot hold them at once, so they are enumerated
    // card-by-card (each preview navigates, WI-6). Never blocks the Un-Preview-All flip. Recomputed
    // every CD (default-CD component), so hand-previewing the odd-date members re-enables the bar.
    get isLoadAllBlocked(): boolean {
        return loadAllGateBlocked(this.members);
    }

    get loadAllDisabledReason(): string {
        return 'Results Span ' + this.unpreviewedExpirationCount + ' Expirations — Preview One-by-One';
    }

    // trackBy = suggestion id (host-minted, §9) so in-place previewed-state mutations never
    // re-create a card (re-creation would tear down the card's live quote subscriptions).
    trackByMemberId(index: number, member: OpgTradeSuggestion): string | number {
        return member ? member.id : index;
    }

    // Bubble the member card's event unchanged.
    @DetectMethodChanges()
    onMemberPreviewToggled(member: OpgTradeSuggestion): void {
        this.previewToggled.emit(member);
    }

    // ONE flipping button (sketch frame 04): which intent it carries is decided by the
    // derived state at click time. dx-button onClick only fires on user clicks — no
    // programmatic-echo hazard here.
    @DetectMethodChanges()
    onLoadAllClicked(): void {
        // isLoadAllBlocked implies NOT fully previewed (the load direction), so this guard never
        // swallows an Un-Preview-All click — it only refuses a mixed-expiration Load-All that a
        // stale [disabled] state might still let through (the button is disabled for it, §WI-7).
        if (!this.hasMembers || this.isLoadAllBlocked) {
            return;
        }
        if (this.isFullyPreviewed) {
            this.unPreviewAllRequested.emit(this.members);
        } else {
            this.loadAllRequested.emit(this.members);
        }
    }
}
